/**
 * Event Detail page -- overview, participants, uploads with tab navigation
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  Users,
  Video,
  CheckCircle2,
  ExternalLink,
  Copy,
  Loader2,
  Play,
  Package,
  MapPin,
  Clock,
  ArrowRight,
  ChevronRight,
  Pencil,
  Trash2,
  Plus,
  CopyPlus,
  Download,
  Timer,
  Upload,
} from "lucide-react";
import {
  getEvent,
  getParticipants,
  getUploads,
  activateEvent,
  closeEvent,
  updateEvent,
  deleteEvent,
  requestUpload,
  createMultipartUpload,
  uploadPart,
  completeMultipartUpload,
} from "@/lib/api";
import type { MixParticipant, CreateEventInput, EventType, OutputTarget } from "@/lib/store";
import { getCaptureUrl } from "@/lib/config";
import { cn, formatDate, formatDateRange, formatDuration, formatBytes } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { EventTypeSelector } from "@/components/EventTypeSelector";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

const statusConfig: Record<
  string,
  { label: string; variant: "secondary" | "success" | "warning" | "default" | "outline" }
> = {
  draft: { label: "Draft", variant: "secondary" },
  active: { label: "Active", variant: "success" },
  closed: { label: "Closed", variant: "warning" },
  producing: { label: "Producing", variant: "default" },
  complete: { label: "Complete", variant: "outline" },
};

type Tab = "overview" | "participants" | "uploads";

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return "";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function useCountdown(targetUnix: number): number {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, targetUnix - Math.floor(Date.now() / 1000))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.max(0, targetUnix - Math.floor(Date.now() / 1000)));
    }, 30_000); // Update every 30s
    return () => clearInterval(interval);
  }, [targetUnix]);

  return remaining;
}

function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const escape = (v: string) => {
    if (v.includes(",") || v.includes('"') || v.includes("\n")) {
      return `"${v.replace(/"/g, '""')}"`;
    }
    return v;
  };
  const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [participantPage, setParticipantPage] = useState(0);
  const [uploadPage, setUploadPage] = useState(0);
  const PAGE_SIZE = 50;

  const {
    data: event,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["event", id],
    queryFn: () => getEvent(id!),
    enabled: !!id,
  });

  const isActive = event?.status === "active" || event?.status === "recording";

  const { data: participantsResponse } = useQuery({
    queryKey: ["participants", id, participantPage],
    queryFn: () => getParticipants(id!, { limit: PAGE_SIZE, offset: participantPage * PAGE_SIZE }),
    enabled: !!id && (activeTab === "participants" || isActive),
    refetchInterval: (activeTab === "participants" || isActive) ? 10_000 : false,
  });
  const participants = participantsResponse?.data;
  const participantsTotal = participantsResponse?.total ?? 0;

  const { data: uploadsResponse } = useQuery({
    queryKey: ["uploads", id, uploadPage],
    queryFn: () => getUploads(id!, { limit: PAGE_SIZE, offset: uploadPage * PAGE_SIZE }),
    enabled: !!id && activeTab === "uploads",
  });
  const uploads = uploadsResponse?.data;
  const uploadsTotal = uploadsResponse?.total ?? 0;

  const activateMutation = useMutation({
    mutationFn: () => activateEvent(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => closeEvent(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteEvent(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      navigate("/");
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "Failed to delete event");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateEventInput>) => updateEvent(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setEditOpen(false);
    },
  });

  // ---- Organizer file upload state ----
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<
    { name: string; progress: number; status: "uploading" | "done" | "error"; error?: string }[]
  >([]);
  const isUploading = uploadingFiles.some((f) => f.status === "uploading");

  const handleOrganizerUpload = useCallback(
    async (files: FileList) => {
      if (!id) return;
      const fileArray = Array.from(files);
      setUploadingFiles(fileArray.map((f) => ({ name: f.name, progress: 0, status: "uploading" })));

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        try {
          // 1. Request upload
          const { fileId, participantId, chunkSize } = await requestUpload(id, {
            filename: file.name,
            contentType: file.type || "video/mp4",
            sizeBytes: file.size,
          });

          // 2. Create multipart upload
          const { uploadId } = await createMultipartUpload(id, fileId);

          // 3. Upload chunks
          const totalChunks = Math.ceil(file.size / chunkSize);
          const parts: { partNumber: number; etag: string }[] = [];

          for (let chunk = 0; chunk < totalChunks; chunk++) {
            const start = chunk * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const blob = file.slice(start, end);

            const { etag } = await uploadPart(id, fileId, uploadId, chunk + 1, blob);
            parts.push({ partNumber: chunk + 1, etag });

            // Update progress
            const progress = Math.round(((chunk + 1) / totalChunks) * 100);
            setUploadingFiles((prev) =>
              prev.map((f, idx) => (idx === i ? { ...f, progress } : f))
            );
          }

          // 4. Complete
          await completeMultipartUpload(id, fileId, {
            uploadId,
            parts,
            participantId,
            sizeBytes: file.size,
          });

          setUploadingFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: "done", progress: 100 } : f))
          );
        } catch (err) {
          setUploadingFiles((prev) =>
            prev.map((f, idx) =>
              idx === i
                ? { ...f, status: "error", error: err instanceof Error ? err.message : "Upload failed" }
                : f
            )
          );
        }
      }

      // Refresh uploads list
      queryClient.invalidateQueries({ queryKey: ["uploads", id] });
    },
    [id, queryClient]
  );

  const startsIn = useCountdown(event?.startsAt ?? 0);
  const endsIn = useCountdown(event?.endsAt ?? 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-vizimix-500" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-sm text-destructive">
        Failed to load event
      </div>
    );
  }

  const config = statusConfig[event.status] || {
    label: event.status,
    variant: "secondary" as const,
  };

  // Construct event URL for QR code — points to capture app
  const captureUrl = getCaptureUrl();
  const eventUrl = `${captureUrl}/e/${event.slug}`;

  function handleCopyUrl() {
    navigator.clipboard.writeText(eventUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleCopyCode() {
    if (event?.joinCode) {
      navigator.clipboard.writeText(event.joinCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "participants", label: "Participants" },
    { key: "uploads", label: "Uploads" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="mt-1 shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-vizimix-100 break-words">
                {event.title}
              </h2>
              <Badge variant={config.variant}>{config.label}</Badge>
              {event.eventType && event.eventType !== "general" && (
                <Badge variant="outline" className="capitalize">
                  {event.eventType}
                </Badge>
              )}
            </div>
            {event.description && (
              <p className="text-sm text-vizimix-400 mt-1">
                {event.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-11 sm:ml-0 shrink-0">
          <Button
            variant="outline"
            onClick={() => navigate(`/events/new?from=${id}`)}
            className="gap-2"
          >
            <CopyPlus className="h-4 w-4" />
            Duplicate
          </Button>
          {(event.status === "draft" || event.status === "active") && (
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          {event.status === "closed" && (
            <Button
              variant="outline"
              onClick={() => navigate(`/events/${id}/produce`)}
              className="gap-2"
            >
              <Package className="h-4 w-4" />
              Produce
            </Button>
          )}
          {(event.status === "producing" || event.status === "completed") && (
            <Button
              variant="outline"
              onClick={() => navigate(`/events/${id}/outputs`)}
              className="gap-2"
            >
              <Play className="h-4 w-4" />
              View Outputs
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-organize-border mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
              activeTab === tab.key
                ? "border-vizimix-500 text-vizimix-100"
                : "border-transparent text-vizimix-400 hover:text-vizimix-200"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR Code */}
          <Card>
            <CardHeader>
              <CardTitle>Event Link</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white p-4 rounded-xl">
                  <QRCodeSVG value={eventUrl} size={200} />
                </div>
                <div className="flex items-center gap-2 w-full">
                  <Input
                    readOnly
                    value={eventUrl}
                    className="text-xs font-mono"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyUrl}
                  >
                    {copied ? (
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(eventUrl, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                {event.joinCode && (
                  <div className="flex flex-col items-center gap-2 pt-2 border-t border-organize-border w-full">
                    <p className="text-xs text-vizimix-400">Or share the join code:</p>
                    <div className="flex items-center gap-2">
                      <span className="text-3xl font-mono font-bold tracking-[0.3em] text-vizimix-100 uppercase">
                        {event.joinCode}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyCode}
                      >
                        {codeCopied ? (
                          <CheckCircle2 className="h-4 w-4 text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stats & Actions */}
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Users className="h-5 w-5 text-vizimix-400" />
                    {isActive && (
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-vizimix-100">
                    {participants ? participants.length : (event.participantCount ?? 0)}
                  </p>
                  <p className="text-xs text-vizimix-400">Participants</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Video className="h-5 w-5 mx-auto mb-1 text-vizimix-400" />
                  <p className="text-2xl font-bold text-vizimix-100">
                    {event.uploadCount ?? 0}
                  </p>
                  <p className="text-xs text-vizimix-400">Uploads</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-vizimix-400" />
                  <p className="text-2xl font-bold text-vizimix-100">
                    {event.approvedCount ?? 0}
                  </p>
                  <p className="text-xs text-vizimix-400">Approved</p>
                </CardContent>
              </Card>
            </div>

            {/* Event Details */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-vizimix-300">
                  <Clock className="h-4 w-4 text-vizimix-500" />
                  {formatDateRange(event.startsAt, event.endsAt)}
                </div>
                {/* Schedule countdown */}
                {event.status === "draft" && startsIn > 0 && (
                  <div className="flex items-center gap-2 text-sm text-vizimix-300">
                    <Timer className="h-4 w-4 text-vizimix-500" />
                    Starts in {formatCountdown(startsIn)}
                  </div>
                )}
                {event.status === "draft" && startsIn === 0 && (
                  <div className="flex items-center gap-2 text-sm text-amber-300">
                    <Timer className="h-4 w-4 text-amber-400" />
                    Event start time has passed — activate now
                  </div>
                )}
                {isActive && endsIn > 0 && (
                  <div className="flex items-center gap-2 text-sm text-vizimix-300">
                    <Timer className="h-4 w-4 text-vizimix-500" />
                    Ends in {formatCountdown(endsIn)}
                  </div>
                )}
                {isActive && endsIn === 0 && (
                  <div className="flex items-center gap-2 text-sm text-amber-300">
                    <Timer className="h-4 w-4 text-amber-400" />
                    Event end time has passed — consider closing
                  </div>
                )}
                {(event.geoLatitude || event.geoLongitude) && (
                  <div className="flex items-center gap-2 text-sm text-vizimix-300">
                    <MapPin className="h-4 w-4 text-vizimix-500" />
                    {event.geoLatitude}, {event.geoLongitude} (
                    {event.geoRadiusMeters}m radius)
                    {event.geoRequired && (
                      <Badge variant="outline" className="ml-1 text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                )}
                <div className="text-sm text-vizimix-400">
                  Max {event.maxParticipants} participants, {formatDuration(event.maxRecordingSeconds)} max recording, {event.maxFileSizeMb}MB max file
                </div>
                {event.recordingPrompt && (
                  <div className="text-sm text-vizimix-300 italic border-t border-organize-border pt-2 mt-2">
                    &ldquo;{event.recordingPrompt}&rdquo;
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Workflow Steps */}
            <Card>
              <CardContent className="p-4 space-y-3">
                {event.status === "draft" && (
                  <Button
                    onClick={() => activateMutation.mutate()}
                    disabled={activateMutation.isPending}
                    className="w-full gap-2"
                  >
                    {activateMutation.isPending && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Activate Event
                  </Button>
                )}
                {(event.status === "active" ||
                  event.status === "recording") && (
                  <Button
                    variant="outline"
                    onClick={() => closeMutation.mutate()}
                    disabled={closeMutation.isPending}
                    className="w-full gap-2"
                  >
                    {closeMutation.isPending && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Close Event
                  </Button>
                )}

                {/* Workflow navigation — always visible once event is closed */}
                {(event.status === "closed" ||
                  event.status === "producing" ||
                  event.status === "completed") && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-vizimix-400 uppercase tracking-wider">
                      Workflow
                    </p>
                    <WorkflowStep
                      label="Review Uploads"
                      description={`${event.approvedCount ?? 0} approved of ${event.uploadCount ?? 0}`}
                      icon={<Video className="h-4 w-4" />}
                      done={(event.approvedCount ?? 0) > 0}
                      active={event.status === "closed" && (event.approvedCount ?? 0) === 0}
                      onClick={() => navigate(`/events/${id}/review`)}
                    />
                    <ChevronRight className="h-4 w-4 mx-auto text-vizimix-600" />
                    <WorkflowStep
                      label="Produce Videos"
                      description="Configure outputs and start production"
                      icon={<Package className="h-4 w-4" />}
                      done={event.status === "producing" || event.status === "completed"}
                      active={event.status === "closed" && (event.approvedCount ?? 0) > 0}
                      onClick={() => navigate(`/events/${id}/produce`)}
                    />
                    <ChevronRight className="h-4 w-4 mx-auto text-vizimix-600" />
                    <WorkflowStep
                      label="View Outputs"
                      description="Download and share finished videos"
                      icon={<Play className="h-4 w-4" />}
                      done={event.status === "completed"}
                      active={event.status === "producing" || event.status === "completed"}
                      onClick={() => navigate(`/events/${id}/outputs`)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Participants Tab */}
      {activeTab === "participants" && (
        <div className="space-y-4">
        {participants && participants.length > 0 && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => {
                const headers = ["Name", "Email", "Status", "Geo Verified", "Duration (s)", "Joined"];
                const rows = participants.map((p) => [
                  p.displayName || "Anonymous",
                  p.email || "",
                  p.recordingStatus,
                  p.geoVerified ? "Yes" : "No",
                  p.recordingDurationSeconds?.toString() || "",
                  new Date(p.joinedAt * 1000).toISOString(),
                ]);
                downloadCsv(`${event.slug}-participants.csv`, headers, rows);
              }}
            >
              <Download className="h-3 w-3" />
              Export CSV
            </Button>
          </div>
        )}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Geo Verified</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {participants && participants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-vizimix-400 py-8">
                    No participants yet
                  </TableCell>
                </TableRow>
              )}
              {participants?.map((p: MixParticipant) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">
                    {p.displayName || "Anonymous"}
                  </TableCell>
                  <TableCell className="text-vizimix-400">
                    {p.email || "-"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        p.recordingStatus === "uploaded"
                          ? "success"
                          : p.recordingStatus === "recording"
                            ? "warning"
                            : "secondary"
                      }
                    >
                      {p.recordingStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {p.geoVerified ? (
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                    ) : (
                      <span className="text-vizimix-500">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.recordingDurationSeconds
                      ? formatDuration(p.recordingDurationSeconds)
                      : "-"}
                  </TableCell>
                  <TableCell className="text-vizimix-400 text-sm">
                    {formatDate(p.joinedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {participantsTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-organize-border">
              <span className="text-sm text-vizimix-400">
                Showing {participantPage * PAGE_SIZE + 1}–{Math.min((participantPage + 1) * PAGE_SIZE, participantsTotal)} of {participantsTotal}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={participantPage === 0}
                  onClick={() => setParticipantPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={(participantPage + 1) * PAGE_SIZE >= participantsTotal}
                  onClick={() => setParticipantPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
        </div>
      )}

      {/* Uploads Tab */}
      {activeTab === "uploads" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-vizimix-400">
              {uploadsTotal} uploads total
            </p>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleOrganizerUpload(e.target.files);
                    e.target.value = "";
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Upload className="h-3 w-3" />
                )}
                Upload Files
              </Button>
              {uploads && uploads.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => {
                    const headers = ["Participant", "Duration (s)", "Size (bytes)", "Status", "Note", "Uploaded"];
                    const rows = uploads.map((u) => [
                      u.participantName || "Unknown",
                      u.durationSeconds?.toString() || "",
                      u.fileSizeBytes?.toString() || "",
                      u.moderationStatus,
                      u.moderationNote || "",
                      new Date(u.uploadedAt * 1000).toISOString(),
                    ]);
                    downloadCsv(`${event.slug}-uploads.csv`, headers, rows);
                  }}
                >
                  <Download className="h-3 w-3" />
                  Export CSV
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/events/${id}/review`)}
                className="gap-2"
              >
                <Video className="h-4 w-4" />
                Open Review Page
              </Button>
            </div>
          </div>
          {uploadingFiles.length > 0 && (
            <div className="mb-4 space-y-2">
              {uploadingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="truncate max-w-[200px] text-vizimix-300">{f.name}</span>
                  <div className="flex-1 h-2 bg-vizimix-800 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        f.status === "error" ? "bg-red-500" : f.status === "done" ? "bg-green-500" : "bg-blue-500"
                      )}
                      style={{ width: `${f.progress}%` }}
                    />
                  </div>
                  <span className="text-vizimix-400 w-12 text-right">
                    {f.status === "error" ? "Error" : f.status === "done" ? "Done" : `${f.progress}%`}
                  </span>
                </div>
              ))}
            </div>
          )}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploads && uploads.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-vizimix-400 py-8"
                    >
                      No uploads yet
                    </TableCell>
                  </TableRow>
                )}
                {uploads?.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      {u.participantName || "Unknown"}
                    </TableCell>
                    <TableCell>
                      {u.durationSeconds
                        ? formatDuration(u.durationSeconds)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {u.fileSizeBytes ? formatBytes(u.fileSizeBytes) : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          u.moderationStatus === "approved"
                            ? "success"
                            : u.moderationStatus === "rejected"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {u.moderationStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-vizimix-400 text-sm">
                      {formatDate(u.uploadedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {uploadsTotal > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-organize-border">
                <span className="text-sm text-vizimix-400">
                  Showing {uploadPage * PAGE_SIZE + 1}–{Math.min((uploadPage + 1) * PAGE_SIZE, uploadsTotal)} of {uploadsTotal}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={uploadPage === 0}
                    onClick={() => setUploadPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={(uploadPage + 1) * PAGE_SIZE >= uploadsTotal}
                    onClick={() => setUploadPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Edit Event Dialog */}
      {editOpen && event && (
        <EditEventDialog
          event={event}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSave={(data) => updateMutation.mutate(data)}
          isLoading={updateMutation.isPending}
          error={updateMutation.error}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Event"
        description={`Are you sure you want to delete "${event.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDatetimeLocal(unix: number): string {
  const d = new Date(unix * 1000);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function toUnixSeconds(datetimeLocal: string): number {
  return Math.floor(new Date(datetimeLocal).getTime() / 1000);
}

// ---------------------------------------------------------------------------
// Edit Event Dialog
// ---------------------------------------------------------------------------

function EditEventDialog({
  event,
  open,
  onClose,
  onSave,
  isLoading,
  error,
}: {
  event: {
    title: string;
    description: string | null;
    eventType: EventType;
    startsAt: number;
    endsAt: number;
    geoLatitude: number | null;
    geoLongitude: number | null;
    geoRadiusMeters: number;
    geoRequired: boolean;
    maxParticipants: number;
    maxRecordingSeconds: number;
    maxFileSizeMb: number;
    outputTargets: OutputTarget[] | null;
    recordingPrompt: string | null;
  };
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<CreateEventInput>) => void;
  isLoading: boolean;
  error: Error | null;
}) {
  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description || "");
  const [eventType, setEventType] = useState<EventType>(event.eventType);
  const [startsAt, setStartsAt] = useState(toDatetimeLocal(event.startsAt));
  const [endsAt, setEndsAt] = useState(toDatetimeLocal(event.endsAt));
  const [geoLatitude, setGeoLatitude] = useState(event.geoLatitude?.toString() || "");
  const [geoLongitude, setGeoLongitude] = useState(event.geoLongitude?.toString() || "");
  const [geoRadiusMeters, setGeoRadiusMeters] = useState(event.geoRadiusMeters);
  const [geoRequired, setGeoRequired] = useState(event.geoRequired);
  const [maxParticipants, setMaxParticipants] = useState(event.maxParticipants);
  const [maxRecordingSeconds, setMaxRecordingSeconds] = useState(event.maxRecordingSeconds);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(event.maxFileSizeMb);
  const [outputTargets, setOutputTargets] = useState<
    { style: OutputTarget["style"]; count: number }[]
  >(event.outputTargets ?? [{ style: "tiktok", count: 1 }]);
  const [recordingPrompt, setRecordingPrompt] = useState(event.recordingPrompt || "");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;

    const data: Partial<CreateEventInput> = {
      title: title.trim(),
      description: description.trim() || undefined,
      eventType,
      startsAt: toUnixSeconds(startsAt),
      endsAt: toUnixSeconds(endsAt),
      geoRequired,
      geoRadiusMeters,
      maxParticipants,
      maxRecordingSeconds,
      maxFileSizeMb,
      outputTargets: outputTargets.length > 0 ? outputTargets : undefined,
      recordingPrompt: recordingPrompt.trim() || undefined,
    };

    if (geoLatitude) data.geoLatitude = parseFloat(geoLatitude);
    if (geoLongitude) data.geoLongitude = parseFloat(geoLongitude);

    onSave(data);
  }

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogContent>
        <DialogHeader onClose={onClose}>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody>
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive">
                  {error.message}
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-vizimix-200 mb-2">
                    Title *
                  </label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vizimix-200 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="flex w-full rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 placeholder:text-vizimix-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500"
                  />
                </div>
                <EventTypeSelector value={eventType} onChange={setEventType} />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vizimix-200 mb-2">
                    Start
                  </label>
                  <Input
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vizimix-200 mb-2">
                    End
                  </label>
                  <Input
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Limits */}
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-vizimix-200 mb-2">
                      Max Participants
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={10000}
                      value={maxParticipants}
                      onChange={(e) => setMaxParticipants(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-vizimix-200 mb-2">
                      Max Recording (s)
                    </label>
                    <Input
                      type="number"
                      min={30}
                      max={300}
                      value={maxRecordingSeconds}
                      onChange={(e) => setMaxRecordingSeconds(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-vizimix-200 mb-2">
                      Max File (MB)
                    </label>
                    <Input
                      type="number"
                      min={10}
                      max={2000}
                      value={maxFileSizeMb}
                      onChange={(e) => setMaxFileSizeMb(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              {/* Geo */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-vizimix-200">Location</p>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="number"
                    step="any"
                    value={geoLatitude}
                    onChange={(e) => setGeoLatitude(e.target.value)}
                    placeholder="Latitude"
                  />
                  <Input
                    type="number"
                    step="any"
                    value={geoLongitude}
                    onChange={(e) => setGeoLongitude(e.target.value)}
                    placeholder="Longitude"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vizimix-200 mb-1">
                    Radius: {geoRadiusMeters}m
                  </label>
                  <input
                    type="range"
                    min={100}
                    max={5000}
                    step={100}
                    value={geoRadiusMeters}
                    onChange={(e) => setGeoRadiusMeters(Number(e.target.value))}
                    className="w-full accent-vizimix-600"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-vizimix-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={geoRequired}
                    onChange={(e) => setGeoRequired(e.target.checked)}
                    className="rounded border-organize-border accent-vizimix-600"
                  />
                  Require location verification
                </label>
              </div>

              {/* Recording Prompt */}
              <div>
                <label className="block text-sm font-medium text-vizimix-200 mb-2">
                  Recording Prompt
                </label>
                <textarea
                  value={recordingPrompt}
                  onChange={(e) => setRecordingPrompt(e.target.value)}
                  placeholder="e.g. &quot;Show us your best dance move!&quot;"
                  rows={2}
                  maxLength={200}
                  className="flex w-full rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 placeholder:text-vizimix-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500"
                />
                <p className="text-xs text-vizimix-500 mt-1">
                  Shown to participants during recording.
                </p>
              </div>

              {/* Output Targets */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-vizimix-200">Output Targets</p>
                {outputTargets.map((target, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <select
                      value={target.style}
                      onChange={(e) => {
                        const updated = [...outputTargets];
                        updated[index] = { ...updated[index], style: e.target.value as OutputTarget["style"] };
                        setOutputTargets(updated);
                      }}
                      className="flex h-10 rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500"
                    >
                      <option value="tiktok">TikTok</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube_short">YouTube Short</option>
                    </select>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={target.count}
                      onChange={(e) => {
                        const updated = [...outputTargets];
                        updated[index] = { ...updated[index], count: Number(e.target.value) };
                        setOutputTargets(updated);
                      }}
                      className="w-24"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setOutputTargets(outputTargets.filter((_, i) => i !== index))}
                      disabled={outputTargets.length <= 1}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOutputTargets([...outputTargets, { style: "tiktok", count: 1 }])}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Output
                </Button>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function WorkflowStep({
  label,
  description,
  icon,
  done,
  active,
  onClick,
}: {
  label: string;
  description: string;
  icon: React.ReactNode;
  done: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
        active
          ? "bg-vizimix-500/10 border border-vizimix-500/30 text-vizimix-100"
          : done
            ? "bg-emerald-500/10 border border-emerald-500/20 text-vizimix-200"
            : "bg-organize-bg border border-organize-border text-vizimix-400 hover:text-vizimix-200"
      )}
    >
      <div
        className={cn(
          "rounded-full p-2",
          active ? "bg-vizimix-500/20" : done ? "bg-emerald-500/20" : "bg-organize-card"
        )}
      >
        {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-vizimix-400 truncate">{description}</p>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0" />
    </button>
  );
}

